import { createDiagnosticsExportButton } from '../diagnostics-export';
import './main-menu.css';
import { createDifficultySelect } from './difficulty-select';
import { createMenuAudio, type MenuAudioState } from './menu-audio';
import type { DifficultyPreset } from '../../simulation/ai/difficulty';
import { createLoadProgress } from './load-progress';
import type { BattleLoadProgress } from '../loading/battle-load-progress';

export interface MainMenuOptions {
  mount: HTMLElement;
  onStart: (opts: { difficulty: DifficultyPreset }) => void;
  requestDiagnosticsExport?: () => Promise<string | undefined>;
}

export interface MainMenu {
  show(): void;
  hide(): void;
  finishForBattle(): Promise<void>;
  setLoading(loading: boolean): void;
  setLoadProgress(progress: BattleLoadProgress): void;
  showLoadError(): void;
}

export function createMainMenu(opts: MainMenuOptions): MainMenu {
  const doc = opts.mount.ownerDocument;
  const element = <K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className: string,
    text?: string,
  ): HTMLElementTagNameMap[K] => {
    const node = doc.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const root = element('section', 'main-menu');
  root.lang = 'ru';
  root.dataset.testid = 'main-menu';
  root.setAttribute('aria-label', 'Главное меню');

  const scenery = element('div', 'main-menu-scenery');
  scenery.setAttribute('aria-hidden', 'true');
  scenery.append(
    element('div', 'main-menu-background'),
    element('div', 'main-menu-haze'),
    element('div', 'main-menu-dust'),
    element('div', 'main-menu-shade'),
  );

  const content = element('div', 'main-menu-content');
  const title = element('h1', 'main-menu-title');
  title.append(element('span', '', 'Fall'), ' ', element('span', '', 'Protocol'));

  const footer = element('footer', 'main-menu-footer');
  const soundButton = element('button', 'main-menu-sound');
  soundButton.type = 'button';
  soundButton.dataset.testid = 'menu-sound';
  const equalizer = element('span', 'main-menu-equalizer');
  equalizer.setAttribute('aria-hidden', 'true');
  equalizer.append(...Array.from({ length: 4 }, () => doc.createElement('i')));
  const soundLabel = element('span', 'main-menu-sound-label', 'Звук');
  const soundState = element('span', 'main-menu-sound-state');
  soundButton.append(equalizer, soundLabel, soundState);
  const musicCredit = element('p', 'main-menu-music-credit');
  const trackCredit = element('a', '', 'Contagion — Scott Buckley');
  trackCredit.href = 'https://www.scottbuckley.com.au/library/contagion/';
  trackCredit.title = 'Трек сокращён и сведён для меню';
  trackCredit.setAttribute('aria-description', trackCredit.title);
  const licenseCredit = element('a', '', 'CC BY 4.0');
  licenseCredit.href = 'https://creativecommons.org/licenses/by/4.0/';
  for (const link of [trackCredit, licenseCredit]) {
    link.target = '_blank';
    link.rel = 'noreferrer noopener';
  }
  const adaptationCredit = element('span', '');
  adaptationCredit.append(licenseCredit);
  musicCredit.append(trackCredit, ' · ', adaptationCredit);
  const utilities = element('div', 'main-menu-utilities');
  utilities.append(soundButton);
  footer.append(utilities, musicCredit);

  const updateSound = (state: MenuAudioState) => {
    root.dataset.sound = state;
    const labels: Record<MenuAudioState, string> = {
      idle: 'Нажмите для звука',
      playing: 'Включён',
      muted: 'Выключен',
      unavailable: 'Повторить',
    };
    soundState.textContent = labels[state];
    soundButton.setAttribute('aria-pressed', String(state === 'idle' || state === 'playing'));
    soundButton.setAttribute(
      'aria-label',
      state === 'unavailable' ? 'Повторить загрузку звука' : `Звук меню: ${labels[state]}`,
    );
    const action: Record<MenuAudioState, string> = {
      idle: 'Включить звук',
      playing: 'Выключить звук',
      muted: 'Включить звук',
      unavailable: 'Повторить загрузку звука',
    };
    soundButton.title = action[state];
  };
  const audio = createMenuAudio({ doc, onStateChange: updateSound });
  updateSound(audio.getState());
  soundButton.addEventListener('click', () => {
    const state = audio.getState();
    if (state === 'unavailable' || state === 'idle') audio.unlock();
    else audio.toggle();
  });
  // Retry blocked autoplay synchronously inside a real user activation. The
  // sound control handles its own intent so muting never immediately unmutes.
  const activateAudio = (event: Event) => {
    if (!root.hidden && !soundButton.contains(event.target as Node | null)) audio.unlock();
  };
  let tabFocusFeedback = false;
  const activateKeyboardAudio = (event: KeyboardEvent) => {
    tabFocusFeedback = event.key === 'Tab';
    activateAudio(event);
  };
  root.addEventListener('pointerdown', () => {
    tabFocusFeedback = false;
  });
  root.addEventListener('click', activateAudio, { capture: true });
  doc.addEventListener('keydown', activateKeyboardAudio);

  let selectedDifficulty: DifficultyPreset = 'normal';
  const difficulty = createDifficultySelect({
    initial: 'normal',
    onChange: (p) => {
      selectedDifficulty = p;
      audio.confirm();
    },
    doc,
  });

  const startBtn = element('button', 'main-menu-start', 'Начать бой');
  startBtn.type = 'button';
  startBtn.dataset.testid = 'menu-start';
  startBtn.addEventListener('click', () => {
    if (startBtn.disabled) return;
    audio.start();
    opts.onStart({ difficulty: selectedDifficulty });
  });
  for (const target of [startBtn, ...difficulty.querySelectorAll('label')]) {
    const hover = () => {
      if (!startBtn.disabled) audio.hover();
    };
    target.addEventListener('pointerenter', (event) => {
      if (!('pointerType' in event) || event.pointerType !== 'touch') hover();
    });
    target.addEventListener('focusin', () => {
      // A pointer click already has its selection cue. Arrow keys also change
      // native radios immediately, so only Tab focus needs separate feedback.
      if (tabFocusFeedback) hover();
    });
  }

  const status = element('p', 'main-menu-status');
  status.hidden = true;
  const loading = createLoadProgress(doc);

  const exportButton = createDiagnosticsExportButton(
    doc,
    'menu-export-diag',
    opts.requestDiagnosticsExport,
  );
  exportButton.className = 'main-menu-export';
  utilities.append(exportButton);
  content.append(title, difficulty, startBtn, status, loading.root);
  root.append(scenery, content, footer);
  opts.mount.appendChild(root);

  const updateVisibility = () => {
    root.classList.toggle(
      'main-menu-suspended',
      root.hidden === true || doc.visibilityState === 'hidden',
    );
  };
  const pageHidden = (event: PageTransitionEvent) => {
    if (event.persisted) return;
    doc.removeEventListener('visibilitychange', updateVisibility);
    doc.removeEventListener('keydown', activateKeyboardAudio);
    doc.defaultView?.removeEventListener('pagehide', pageHidden);
    audio.dispose();
  };
  doc.addEventListener('visibilitychange', updateVisibility);
  doc.defaultView?.addEventListener('pagehide', pageHidden);
  updateVisibility();

  const setLoading = (active: boolean) => {
    root.classList.toggle('is-loading', active);
    // Busy belongs to the controls, not the separate live progress region.
    startBtn.setAttribute('aria-busy', String(active));
    difficulty.setAttribute('aria-busy', String(active));
    startBtn.disabled = active;
    for (const input of difficulty.querySelectorAll('input')) input.disabled = active;
    startBtn.textContent = active ? 'Подготовка боя…' : 'Начать бой';
    status.hidden = true;
    status.removeAttribute('role');
    if (active) {
      root.hidden = false;
      // Keep the menu score through asset preparation. Only the ready-frame
      // handoff fades it out, immediately before battle music may begin.
      loading.update({ phase: 'runtime' });
    } else loading.hide();
    updateVisibility();
  };
  return {
    show() {
      setLoading(false);
      root.hidden = false;
      updateVisibility();
      audio.setVisible(true);
      startBtn.focus();
    },
    hide() {
      setLoading(false);
      root.hidden = true;
      updateVisibility();
      audio.setVisible(false);
    },
    setLoading,
    finishForBattle: () => audio.finishForBattle(),
    setLoadProgress: (progress) => loading.update(progress),
    showLoadError() {
      setLoading(false);
      root.hidden = false;
      startBtn.disabled = false;
      startBtn.textContent = 'Обновить страницу';
      status.hidden = false;
      status.setAttribute('role', 'alert');
      status.textContent =
        'Не удалось загрузить поле боя. Проверьте соединение и обновите страницу. Нужен браузер с WebGL 2.';
      updateVisibility();
      audio.setVisible(true);
      startBtn.focus();
    },
  };
}
